// FILE: src/pages/admin/student/AdminStudentSupportPage.jsx
// One student's support requests and course survey (WP-7; MTO 10.1–10.3,
// 11.2.1–11.2.2). The requests and survey come with the student's
// compliance record (GET /api/admin/compliance-report/:id → tickets,
// feedbacks); each request links to its conversation.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import fetchWithAuth from "../../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, Section, StatusChip, visuallyHidden } from "../../../components/ui";
import { ResponsiveTable, TicketStatusChip } from "../../../components/admin/AdminBits";
import { ChannelText, SlaChip } from "../../../components/admin/supportSla";
import { formatDate, formatDateTime } from "../../../components/admin/format";
import { ticketCategoryLabel } from "../../../i18n/labels";
import { useStudent } from "./AdminStudentLayout";

export default function AdminStudentSupportPage() {
  const { student } = useStudent();
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const latest = useRef(0);

  const load = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/compliance-report/${encodeURIComponent(student._id)}`)
      .then((data) => {
        if (req === latest.current) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [student._id]);

  useEffect(() => {
    load();
  }, [load]);

  const { data, error } = state;
  if (!data) {
    return error ? (
      <ErrorState error={error} title="We couldn't load the support requests" onRetry={load} />
    ) : (
      <LoadingState variant="table" rows={3} label="Loading the support requests…" />
    );
  }

  const tickets = (Array.isArray(data.tickets) ? data.tickets : [])
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const surveys = (Array.isArray(data.feedbacks) ? data.feedbacks : [])
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const survey = surveys[0] || null;
  const finished = !!student.progressSummary?.courseComplete;

  return (
    <Stack spacing={2.5}>
      <Section
        id="requests"
        title="Support requests"
        description={`${tickets.length} ${tickets.length === 1 ? "request" : "requests"}, newest first. Every request is answered within 48 hours.`}
        contentSx={{ p: { xs: 2, sm: 3 } }}
      >
        {tickets.length === 0 ? (
          <EmptyState
            compact
            icon={SupportAgentOutlinedIcon}
            title="No support requests"
            description="This student hasn't asked for help. Requests they send, or that staff log for them, appear here."
          />
        ) : (
          <ResponsiveTable label="Support requests from this student" minWidth={760}>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  Kind of help
                </TableCell>
                <TableCell component="th" scope="col">
                  Opened
                </TableCell>
                <TableCell component="th" scope="col">
                  Status
                </TableCell>
                <TableCell component="th" scope="col" align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((t) => (
                <TableRow key={t._id}>
                  <TableCell component="th" scope="row">
                    <Typography component="span" sx={{ display: "block", fontWeight: 600 }}>
                      {ticketCategoryLabel(t.category)}
                    </Typography>
                    <ChannelText channel={t.channel} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(t.createdAt)}</TableCell>
                  <TableCell>
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
                      <TicketStatusChip status={t.status} />
                      <SlaChip ticket={t} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button component={RouterLink} to={`/admin/support/${t._id}`} variant="outlined">
                      View request
                      <Box sx={visuallyHidden}>{` from ${formatDate(t.createdAt)}`}</Box>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )}
      </Section>

      <Section id="survey" title="Course survey" description="Students send the six-topic survey when they've finished every chapter.">
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          {survey ? (
            <StatusChip tone="success" icon={<TaskAltOutlinedIcon />} label={`Sent ${formatDate(survey.createdAt)}`} />
          ) : (
            <StatusChip
              icon={<PendingOutlinedIcon />}
              label={finished ? "Not sent yet" : "Not available yet: course not finished"}
            />
          )}
          {survey && (
            <Button
              component={RouterLink}
              to={`/admin/feedback/${survey._id}`}
              variant="outlined"
              startIcon={<RateReviewOutlinedIcon />}
            >
              View the survey response
            </Button>
          )}
        </Stack>
      </Section>
    </Stack>
  );
}
